/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 *
 * The number of loaded pages is kept in the url hash (e.g. #page=3). The hash never reaches
 * the server, so the canonical url keeps pointing at page 1, while reloading, sharing and
 * navigating back to the url loads the same pages again.
 */
if (!customElements.get('custom-pagination')) {
  // Only the first instance on a document load may restore previously loaded pages
  let restoreChecked = false;

  // Upper limit of pages to restore from the url hash
  const MAX_RESTORE_PAGE = 50;

  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
      window.addEventListener('pagehide', this.saveState.bind(this));

      if (!restoreChecked) {
        restoreChecked = true;
        this.restoreState();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;
      this.clickedResult = null;

      // Bind click handlers to pagination results
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      // Bind click handler to load more button click
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.pagination.dataset.isMoreResults === 'true' && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadMore();

                  // Continue to load results as long as the 'load more' button is visible
                  this.intersectingTimer = setInterval(this.loadMore.bind(this), 1000);
                } else if (this.intersectingTimer) {
                  clearInterval(this.intersectingTimer);
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    /**
     * Destroys the component
     */
    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      this.loadMoreClickHandler = null;
    }

    /**
     * Reloads the component
     */
    reload() {
      this.destroy();
      this.init();
    }

    /**
     * Handles click on the load more button
     * @param {object} evt - Event object
     */
    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Handles a click on a item in the pagination
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
      const link = evt.target.closest('a');
      const result = evt.target.closest('.js-pagination-result');
      if (!link || !result) return;

      // Remember the clicked result, so we can return to it when navigating back
      this.clickedResult = { href: link.href, top: result.getBoundingClientRect().top };

      if (this.controller) this.controller.abort();
    }

    /**
     * Gets the parameters in the url hash, e.g. #page=3. A plain anchor hash
     * (e.g. #products-toolbar after sorting) has no parameters and is dropped.
     * @returns {URLSearchParams}
     */
    static getHashParams() {
      const hash = window.location.hash.slice(1);
      return new URLSearchParams(hash.includes('=') ? hash : '');
    }

    /**
     * Gets the page number of a pagination url
     * @param {string} href - Pagination url
     * @returns {number}
     */
    static getPageNumber(href) {
      return Number.parseInt(new URL(href, window.location.href).searchParams.get('page'), 10) || 1;
    }

    /**
     * Updates the page number in the url hash, without adding a history entry
     * @param {number} page - Last loaded page
     */
    static setPageHash(page) {
      const params = CustomPagination.getHashParams();
      if (page > 1) {
        params.set('page', page);
      } else {
        params.delete('page');
      }

      const hash = params.toString();
      const url = `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ''}`;

      // Keep the history state, it's used by the facet filters
      window.history.replaceState(window.history.state, '', url);
    }

    /**
     * Gets the sessionStorage key for the current listing (incl. filter/sort parameters)
     * @returns {string}
     */
    static getStorageKey() {
      return `custom-pagination:${window.location.pathname}${window.location.search}`;
    }

    /**
     * Saves the clicked result and scroll position when leaving the page
     */
    saveState() {
      try {
        const key = CustomPagination.getStorageKey();
        if (!CustomPagination.getHashParams().get('page')) {
          sessionStorage.removeItem(key);
          return;
        }

        sessionStorage.setItem(key, JSON.stringify({
          clickedResult: this.clickedResult,
          scrollY: window.scrollY
        }));
      } catch (error) {
        // sessionStorage unavailable (e.g. private mode), the pages are still restored from the hash
      }
    }

    /**
     * Loads the pages given in the url hash (e.g. #page=3) on page load
     */
    async restoreState() {
      const targetPage = Math.min(
        Number.parseInt(CustomPagination.getHashParams().get('page'), 10) || 0,
        MAX_RESTORE_PAGE
      );

      if (targetPage < 2 || !this.loadMoreButton || !this.pagination
        || this.pagination.dataset.isMoreResults !== 'true') return;

      const firstPage = CustomPagination.getPageNumber(this.loadMoreButton.href);
      if (targetPage < firstPage) return;

      // The scroll position is only restored when returning to the page (back or reload)
      let state = null;
      try {
        const key = CustomPagination.getStorageKey();
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation && ['back_forward', 'reload'].includes(navigation.type)) {
          state = JSON.parse(sessionStorage.getItem(key));
        }
        sessionStorage.removeItem(key);
      } catch (error) {
        state = null;
      }

      // Prevent the browser from scrolling to the old position before the results are back
      if (state && 'scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

      const hrefs = [];
      for (let page = firstPage; page <= targetPage; page += 1) {
        const url = new URL(this.loadMoreButton.href);
        url.searchParams.set('page', page);
        hrefs.push(url.toString());
      }

      // Block loadMore (incl. infinite scroll) while restoring
      this.isFetching = true;
      this.controller = new AbortController();
      this.loadMoreButton.classList.add('is-loading');
      this.loadMoreButton.setAttribute('aria-disabled', 'true');

      let lastLoadedPage = firstPage - 1;

      try {
        const responses = await Promise.allSettled(hrefs.map(async (href) => {
          const response = await fetch(href, { signal: this.controller.signal });
          if (!response.ok) throw new Error(response.status);
          return response.text();
        }));

        // Append in order, and stop at the first page that failed or the last page of results
        for (let i = 0; i < responses.length; i += 1) {
          if (responses[i].status !== 'fulfilled') break;
          lastLoadedPage = firstPage + i;
          if (!this.appendResults(responses[i].value)) break;
        }
      } catch (error) {
        console.log('Unable to restore previous pages of results', error); // eslint-disable-line
      } finally {
        this.controller = null;
        this.isFetching = false;
        if (this.loadMoreButton) {
          this.loadMoreButton.classList.remove('is-loading');
          this.loadMoreButton.removeAttribute('aria-disabled');
        }
      }

      // Correct the hash if fewer pages than requested could be loaded
      if (lastLoadedPage !== targetPage) CustomPagination.setPageHash(lastLoadedPage);

      if (state) this.scrollToRestoredPosition(state);
    }

    /**
     * Scrolls the previously clicked result back to where it was in the viewport
     * @param {object} state - Saved pagination state
     */
    scrollToRestoredPosition(state) {
      const { clickedResult } = state;
      const link = clickedResult && Array.from(this.querySelectorAll('.js-pagination-result a'))
        .find((elem) => elem.href === clickedResult.href);

      requestAnimationFrame(() => {
        if (link) {
          const result = link.closest('.js-pagination-result');
          window.scrollTo(0, result.getBoundingClientRect().top + window.scrollY - clickedResult.top);
        } else {
          window.scrollTo(0, state.scrollY || 0);
        }
      });
    }

    /**
     * Appends the results of a fetched page and updates the pagination
     * @param {string} html - Markup of the fetched page
     * @returns {boolean} Whether there are more results to load
     */
    appendResults(html) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      // Append the additional results
      let newResultsHtml = '';
      const lastResult = this.results[this.results.length - 1];
      tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
        newResultsHtml += result.outerHTML;
      });
      lastResult.insertAdjacentHTML('afterend', newResultsHtml);
      this.results = this.querySelectorAll('.js-pagination-result');

      // Update the pagination content
      const newPagination = tmpl.content.querySelector('.js-pagination');
      if (newPagination && newPagination.dataset.isMoreResults === 'true') {
        this.pagination.innerHTML = newPagination.innerHTML;

        // Rebind to the new load more button
        if (this.loadMoreButton) {
          this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
          this.loadMoreButton = this.querySelector('.js-pagination-load-more');
          this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
        }

        return true;
      }

      this.pagination.remove();
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());

      return false;
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async loadMore() {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.controller = new AbortController();

        try {
          // Add the loading state to the button
          if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          // Retrieve and parse the next page
          const { href } = this.loadMoreButton;
          const response = await fetch(href, {
            signal: this.controller.signal
          });

          if (!response.ok) throw new Error(response.status);
          if (this.dataset.pauseInfiniteScroll === 'true') {
            throw new Error('Infinite scroll was interrupted.');
          }

          this.appendResults(await response.text());
          CustomPagination.setPageHash(CustomPagination.getPageNumber(href));
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        } finally {
          this.controller = null;

          // Remove the loading state from the load more button
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
